import { askGroq } from "../ai/groqClient";
import { safeText } from "./textHelpers";

// ============================================================
// NARRATIVE GENERATION LAYER
// ============================================================
// Every function here sends Groq ONLY small, pre-computed JSON (metrics /
// findings) — never a single dataset row. Groq's only job is to write
// prose ON TOP of numbers that have already been calculated; it is never
// allowed to introduce a number that isn't already present in what it was
// given. All responses are parsed as JSON for reliability and every string
// field is run through safeText() before it ever reaches the PDF.

const BANNED_PHRASES_RULE = `
Never use these overused AI phrases or anything equivalent to them:
- "The analysis reveals a comprehensive overview..."
- "It is important to note that..."
- "This highlights the importance of..."
- "The findings clearly indicate..." (used repeatedly)
Write like a professional business analyst, not an AI assistant. Short,
direct, confident sentences. No filler.
`;

const EVIDENCE_RULE = `
Every finding below has an "evidenceLevel":
- "fact": state it plainly and directly, as an observed fact.
- "correlation": two things moved together. NEVER say one caused the other.
  Use language like "coincided with" — never "caused by", "due to", "led to",
  "resulted in".
A finding may also include a "caution" field — if present, follow it exactly.
Never upgrade a correlation into a causal claim, no matter how obvious it
looks. If you don't have the data to explain WHY something happened, say so
plainly rather than guessing.
`;

const NO_UNICODE_RULE = `
Do not use arrow characters, triangle symbols, or any non-ASCII symbols.
Use plain words or signed numbers instead: "+5.2%", "-1.6%", "Increased",
"Decreased", "Stable".
`;

function extractJson(text) {
  const match = text?.match(/\{[\s\S]*\}/);
  if (!match) throw new Error("No JSON in model response");
  return JSON.parse(match[0]);
}

function cleanStrings(obj) {
  if (typeof obj === "string") return safeText(obj);
  if (Array.isArray(obj)) return obj.map(cleanStrings);
  if (obj && typeof obj === "object") {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, cleanStrings(v)]));
  }
  return obj;
}

// ---- Finding interpretations (used by the detailed Sales Analysis Report) ----
// One call, batched across all findings, so the PDF never shows two
// findings that quietly repeat the same point.
export async function generateFindingInterpretations(findings) {

  const prompt = `
You are writing the "WHY IT MATTERS" business interpretation for each
finding in a Sales Analysis Report. You are given the FACT for each finding
already calculated — do not restate the fact, just explain its business
significance in 1-2 sentences.

${EVIDENCE_RULE}
${BANNED_PHRASES_RULE}
${NO_UNICODE_RULE}

Do not repeat the same point across different findings — if two findings
would lead to a similar interpretation, make each one add something new or
keep the weaker one brief.

Return ONLY JSON: { "interpretations": [ { "id": "<finding id>", "interpretation": "..." } ] }

Findings:

${JSON.stringify(findings, null, 2)}
`;

  try {

    const response = await askGroq({
      messages: [{ role: "system", content: prompt }],
      temperature: 0.3,
    });

    const parsed = cleanStrings(extractJson(response));
    const map = {};

    for (const item of parsed.interpretations || []) {
      map[item.id] = item.interpretation;
    }

    return map;

  } catch (err) {
    console.error("generateFindingInterpretations failed:", err);
    return {};
  }
}

// ---- Detailed report content: recommendations + next-analysis questions ----
export async function generateDetailedReportContent(metrics, findings) {

  const prompt = `
You are writing two sections of a Sales Analysis Report for department heads
and analysts, based on verified metrics and findings (given below, already
calculated — never invent a number that isn't in this data).

${EVIDENCE_RULE}
${BANNED_PHRASES_RULE}
${NO_UNICODE_RULE}

SECTION 1 — RECOMMENDATIONS (3 to 5 items)
Each must be specific and directly tied to a finding below — never generic
advice like "optimize sales strategies". Reference actual labels/numbers
from the findings (rep names, dimension values, percentages).

SECTION 2 — RECOMMENDED NEXT ANALYSIS (3 to 5 items)
Questions this dataset CANNOT answer but would add business value if
answered — things that need more data/dimensions than what's here.

Return ONLY JSON:
{
  "recommendations": [
    { "title": "...", "action": "...", "reason": "...", "purpose": "..." }
  ],
  "nextAnalysisQuestions": [ "..." ]
}

Metrics:
${JSON.stringify(metrics, null, 2)}

Findings:
${JSON.stringify(findings, null, 2)}
`;

  try {

    const response = await askGroq({
      messages: [{ role: "system", content: prompt }],
      temperature: 0.3,
    });

    return cleanStrings(extractJson(response));

  } catch (err) {
    console.error("generateDetailedReportContent failed:", err);
    return { recommendations: [], nextAnalysisQuestions: [] };
  }
}

// ---- Executive summary content: takeaway + priorities + actions ----
export async function generateExecutiveSummaryContent(metrics, findings) {

  const prompt = `
You are writing an Executive Summary for a CEO/director who has 2 minutes to
read it. Based ONLY on the verified metrics and findings below (already
calculated — never invent a number).

${EVIDENCE_RULE}
${BANNED_PHRASES_RULE}
${NO_UNICODE_RULE}

SECTION 1 — EXECUTIVE TAKEAWAY (3 to 5 sentences)
Answer: what happened, what's the most important insight, what's the primary
business implication, what should management do next. Direct and confident.

SECTION 2 — MANAGEMENT PRIORITIES (exactly the 3 most important, ranked)
Each: a short title and a 1-sentence explanation.

SECTION 3 — MANAGEMENT ACTIONS (exactly 3, concise, directly tied to the
priorities above — one short sentence each, no long paragraphs)

Return ONLY JSON:
{
  "takeaway": "...",
  "priorities": [ { "title": "...", "explanation": "..." } ],
  "actions": [ "..." ]
}

Metrics:
${JSON.stringify(metrics, null, 2)}

Findings:
${JSON.stringify(findings, null, 2)}
`;

  try {

    const response = await askGroq({
      messages: [{ role: "system", content: prompt }],
      temperature: 0.3,
    });

    return cleanStrings(extractJson(response));

  } catch (err) {
    console.error("generateExecutiveSummaryContent failed:", err);
    return { takeaway: "", priorities: [], actions: [] };
  }
}
